'use client';

import * as React from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';

import { useGetSavedEventsQuery, useSaveEventMutation, useUnsaveEventMutation } from '@loci/api';
import { useNotification } from '@loci/store';

import { useAuth } from '@/contexts/AuthContext';
import { usePersonalization } from '@/hooks/usePersonalization';
import { crossHostSignup } from '@/lib/cross-host-redirect';

import { cn } from '@/components/atoms/primitives/utils';
import { SaveEventButton } from '@/components/molecules/SaveEventButton';

const SAVED_EVENTS_PAGE_SIZE = 100;

/**
 * WEB-1076 / FRE-1379 — Discover's save is a 34×34 circle on both pointers,
 * opted into with `size="row"` so every other consumer (the shared
 * `SaveEventButton` in EventSpine; this wrapper in PlaceInfoPanel, UpNext,
 * the map card and ExploreByNeighborhood) keeps its 44px default. Touch gets its 44×44 hit box from an `::after`
 * at `inset: -5px` (34 + 2×5), switched on by the same `--hit-content` token
 * the atoms guard reads: '' on coarse pointers or `[data-pointer='coarse']`,
 * `none` (no box) everywhere else. The `::after`, not the guard's `::before`,
 * per the design: the save's halo has always been its `::after`.
 */
const DISCOVER_ROW_SAVE_CLASSES = cn(
  'relative size-8.5 min-h-0 min-w-0 desktop:size-8.5 desktop:min-h-0 desktop:min-w-0',
  'after:absolute after:-inset-1.25 after:rounded-full after:content-(--hit-content)',
);

export interface DiscoverSaveButtonProps {
  /** Stable event id — used to derive `isSaved` and dispatch save/unsave. */
  eventId: string;
  /** Event slug — used to build the `?action=save` deep-link for anonymous users. */
  eventSlug: string;
  /** Event title — used for screen-reader labelling on the button. */
  eventTitle: string;
  /** Optional class merged onto the underlying button. */
  className?: string;
  /**
   * `row` — the Discover event row's 34×34 circle with a coarse-pointer
   * 44×44 `::after` hit box (WEB-1076). Omit for the 44px default.
   */
  size?: 'default' | 'row';
}

/**
 * Connected save button for Discover surfaces (list rows, map cards, etc).
 *
 * Wires {@link SaveEventButton} into the existing RTK Query mutations
 * (`useSaveEventMutation` / `useUnsaveEventMutation`) and derives `isSaved`
 * from the same shared `useGetSavedEventsQuery({ userId, limit: 100 })`
 * cache the saved-events page and event detail already consume — so every
 * surface that uses the same args dedupes into a single network request
 * (cache key is `{ userId, limit: 100 }`).
 *
 * Anonymous users are redirected to `app.loci.city/signup?next=<event-url>?action=save`
 * via `crossHostSignup` — the same pattern as the event-detail "Save" CTA.
 * The `?action=save` query param is honored by `EventSpineConnected` so the
 * save lands automatically after signup.
 *
 * Failure modes:
 *   - Mutation rejects → optimistic update reverts (RTK Query handles it
 *     in `lociApi.ts`), error toast surfaces via `useNotification`.
 *   - Anonymous click → cross-host signup redirect (no silent fail per
 *     FRE-803 AC).
 */
export function DiscoverSaveButton({
  eventId,
  eventSlug,
  eventTitle,
  className,
  size = 'default',
}: DiscoverSaveButtonProps): React.ReactElement {
  const { isAuthenticated, user } = useAuth();
  const { getSessionId } = usePersonalization();
  const { showNotification } = useNotification();

  // Reuse the same cache key as EventSpineConnected + SavedEventsFullView so
  // the three surfaces share one subscription. RTK Query dedupes on the
  // serialized args object → only one /analytics/user/:id/saved request.
  const { data: savedEventsData } = useGetSavedEventsQuery(
    user?.id ? { userId: user.id, limit: SAVED_EVENTS_PAGE_SIZE } : skipToken,
  );

  const [saveEventMutation, saveState] = useSaveEventMutation();
  const [unsaveEventMutation, unsaveState] = useUnsaveEventMutation();

  const isSaved = isAuthenticated
    ? (savedEventsData?.savedEvents.some((entry) => entry.event?.id === eventId) ?? false)
    : false;

  const isPending = saveState.isLoading || unsaveState.isLoading;

  const handleRequireAuth = React.useCallback(() => {
    // Mirror EventSpineConnected.handleRequireAuthForSave: ship the user to
    // signup with a `?action=save` deep link so the save lands automatically
    // after they finish creating an account.
    const target = crossHostSignup(`/event/${eventSlug}?action=save`);
    window.location.assign(target);
  }, [eventSlug]);

  const handleToggle = React.useCallback(
    async (nextSaved: boolean) => {
      if (!user?.id) {
        // Defensive — SaveEventButton routes anonymous clicks through
        // onRequireAuth, but if isAuthenticated flickers between the click
        // and the await, never fire a mutation without a user id.
        handleRequireAuth();
        return;
      }
      const sessionId = getSessionId();
      try {
        if (nextSaved) {
          await saveEventMutation({ userId: user.id, eventId, sessionId }).unwrap();
        } else {
          await unsaveEventMutation({ userId: user.id, eventId, sessionId }).unwrap();
        }
      } catch {
        showNotification({
          type: 'error',
          title: nextSaved ? 'Failed to save event' : 'Failed to remove saved event',
          message: 'Please try again.',
        });
      }
    },
    [
      user?.id,
      eventId,
      saveEventMutation,
      unsaveEventMutation,
      getSessionId,
      showNotification,
      handleRequireAuth,
    ],
  );

  return (
    <SaveEventButton
      isSaved={isSaved}
      eventTitle={eventTitle}
      onToggle={handleToggle}
      onRequireAuth={handleRequireAuth}
      isAuthenticated={isAuthenticated}
      disabled={isPending}
      className={cn(size === 'row' && DISCOVER_ROW_SAVE_CLASSES, className)}
    />
  );
}
